import { useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { IconBell, IconSortAscending, IconWallet } from "@tabler/icons-react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid2,
  Stack,
  Typography,
} from "@mui/material";

import {
  formatCpfCnpj,
  formatDate,
  formatPaymentStatus,
  formatPaymentType,
  formatPhone,
  formatPrice,
} from "../helpers/format";

import { paymentDetails } from "../services/finance";
import Info from "../components/Info";
import { isEmpty } from "lodash";
import BoxLoading from "../components/BoxLoading";

type FinanceDetailsProps = {
  id: string;
};

const FinanceDetails = ({ id }: FinanceDetailsProps) => {
  const [open, setOpen] = useState(false);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["paymentDetails", id],
    queryFn: () => paymentDetails(id),
    enabled: !!id,
    initialData: { menssages: [] },
  });

  const handleClose = () => {
    setOpen(false);
  };

  const loading = isLoading || isFetching;
  const responsable = data?.user?.responsableId && data?.student;

  return (
    <BoxLoading loading={loading}>
      <Grid2 container spacing={1}>
        <Grid2
          container
          sx={{
            backgroundColor: "secondary.main",
            padding: 2,
            borderRadius: 2,
          }}
          width={600}
        >
          <Grid2 size={12}>
            <Stack flexDirection={"row"} justifyContent={"space-between"}>
              <Stack flexDirection={"row"} gap={1} alignItems={"center"}>
                <IconWallet color="#7600ED" />
                <Typography fontWeight={700} color="primary">
                  Cobrança
                </Typography>{" "}
              </Stack>
              {data?.webToken && data.status !== "DEBITED" && (
                <Button
                  endIcon={<IconWallet />}
                  variant="contained"
                  size="small"
                  onClick={() => {
                    window.open("/i/" + data?.webToken);
                  }}
                >
                  Ver fatura
                </Button>
              )}
            </Stack>
          </Grid2>
          <Grid2 size={12}></Grid2>
          {data.status !== "DEBITED" && (
            <Info
              size={6}
              title={"Status"}
              subTitle={formatPaymentStatus(data?.status)}
            />
          )}
          <Info
            title={"Tipo"}
            subTitle={formatPaymentType(data?.billingType)}
          />
          <Info title={"Valor da fatura"} subTitle={formatPrice(data?.value)} />{" "}
          {data.status !== "DEBITED" && data?.valueNet && (
            <Info
              title={"Valor a receber"}
              subTitle={formatPrice(data?.valueNet)}
              onClick={() => {
                setOpen(true);
              }}
            />
          )}
          {data.status !== "CONFIRMED_INSTALLMENT" && data?.creditDate && (
            <Info
              title={"Previsão de credito"}
              subTitle={formatDate(data?.creditDate)}
            />
          )}
          {data?.paymentDate && (
            <Info
              title={"Data de pagamento"}
              subTitle={formatDate(data?.paymentDate)}
            />
          )}
          {data?.dueDate && (
            <Info title={"Vencimento"} subTitle={formatDate(data?.dueDate)} />
          )}
          {data?.payer?.fullName && (
            <>
              <Grid2 size={12}>
                <Stack flexDirection={"row"} gap={1} alignItems={"center"}>
                  <IconSortAscending color="#7600ED" />
                  <Typography fontWeight={700} color="primary">
                    Dados do pagador
                  </Typography>{" "}
                </Stack>
              </Grid2>
              <Info title={"Nome"} size={6} subTitle={data?.payer?.fullName} />
              <Info
                title={"Documento"}
                size={6}
                subTitle={formatCpfCnpj(data?.payer?.document)}
              />
              <Info
                title={"Telefone"}
                size={6}
                subTitle={formatPhone(data?.payer?.phone)}
              />
              <Info title={"E-mail"} size={6} subTitle={data?.payer?.email} />
            </>
          )}
          {data?.description && (
            <Info size={12} title={"Descrição"} subTitle={data?.description} />
          )}{" "}
          {responsable && (
            <>
              <Grid2 size={12}>
                <Stack flexDirection={"row"} gap={1} alignItems={"center"}>
                  <IconSortAscending color="#7600ED" />
                  <Typography fontWeight={700} color="primary">
                    Dados do aluno
                  </Typography>{" "}
                </Stack>
              </Grid2>{" "}
              <Info
                title={"Aluno"}
                size={6}
                subTitle={data?.student?.fullName}
              />
            </>
          )}
          {data?.paymentNotification && (
            <>
              <Grid2 size={12}>
                <Divider />
              </Grid2>
              <Grid2 size={12}>
                <Stack flexDirection={"row"} justifyContent={"space-between"}>
                  <Stack flexDirection={"row"} gap={1} alignItems={"center"}>
                    <IconBell color="#7600ED" />
                    <Typography fontWeight={700} color="primary">
                      Cobrança da notificação
                    </Typography>
                  </Stack>
                </Stack>
              </Grid2>
              <Info
                title={"Nome"}
                size={6}
                subTitle={data?.paymentNotification?.fullName}
              />
              <Info
                size={6}
                title={"Status"}
                subTitle={formatPaymentStatus(
                  data?.paymentNotification?.status
                )}
              />
              <Info
                title={"Telefone"}
                size={6}
                subTitle={formatPhone(data?.paymentNotification?.phone)}
              />
              <Info
                title={"Vencimento"}
                size={6}
                subTitle={formatDate(data?.paymentNotification?.dueDate)}
              />
              <Info
                title={"Descrição do pagamento"}
                size={12}
                subTitle={data?.paymentNotification?.description}
              />
            </>
          )}
          {data?.menssages && !isEmpty(data?.menssages) && (
            <>
              <Grid2 size={12}>
                <Divider />
              </Grid2>
              <Grid2 size={12}>
                <Stack flexDirection={"row"} gap={1} alignItems={"center"}>
                  <IconBell color="#7600ED" />
                  <Typography fontWeight={700} color="primary">
                    Histórico
                  </Typography>{" "}
                </Stack>
              </Grid2>
              {data?.menssages?.map((i: any) => (
                <Grid2 size={12}>
                  <Stack flexDirection={"row"} gap={1} alignItems={"center"}>
                    <Typography variant="body2" fontWeight={500}>
                      {i?.menssage}
                    </Typography>{" "}
                  </Stack>
                  <Divider sx={{ mt: 2 }} />
                </Grid2>
              ))}
            </>
          )}
        </Grid2>
      </Grid2>

      <Dialog onClose={handleClose} open={open} fullWidth maxWidth="xs">
        <DialogTitle>Valor a receber</DialogTitle>
        <DialogContent>
          <Stack sx={{ gap: 1 }}>
            <Stack justifyContent={"space-between"} direction={"row"}>
              <Typography color="textSecondary">Valor pago</Typography>
              <Typography>{formatPrice(data.value)}</Typography>
            </Stack>
            <Stack justifyContent={"space-between"} direction={"row"}>
              <Typography color="textSecondary">Tarifa</Typography>
              <Typography>- {formatPrice(data.valueTax)}</Typography>
            </Stack>{" "}
            {Boolean(data.valueAnticipation) && (
              <Stack justifyContent={"space-between"} direction={"row"}>
                <Typography color="textSecondary">Antecipação</Typography>
                <Typography>-{formatPrice(data.valueAnticipation)}</Typography>
              </Stack>
            )}
            {Boolean(data.valueTaxNotification) && (
              <Stack justifyContent={"space-between"} direction={"row"}>
                <Typography color="textSecondary">Mensageria</Typography>
                <Typography>
                  -{formatPrice(data.valueTaxNotification)}
                </Typography>
              </Stack>
            )}{" "}
            <Stack justifyContent={"space-between"} direction={"row"}>
              <Typography fontWeight={700}>Total</Typography>
              <Typography
                color={data.valueNet > 0 ? "success" : "error"}
                fontWeight={700}
              >
                {formatPrice(data.valueNet)}
              </Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleClose}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </BoxLoading>
  );
};

export default FinanceDetails;
